import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { useTranslation } from 'react-i18next'

// Cache the current user's following list across remounts so follow buttons
// don't flash "…" every time the leaderboard (or profile panel) loads.
let lastFollowingIds: string[] | null = null

export function useFollowToggle(handle: string | null, userId: string, initial?: boolean) {
  // One shared subscription for every button (Convex dedupes identical args).
  const liveIds = useQuery(api.follows.getMyFollowingIds)
  if (liveIds !== undefined) lastFollowingIds = liveIds
  const ids = liveIds ?? lastFollowingIds

  const follow = useMutation(api.follows.follow).withOptimisticUpdate((store) => {
    // Fall back to the cached list (what the buttons render from) when the live
    // query hasn't resolved yet — otherwise the base is [] and setting it wipes
    // every other followed id, flashing those buttons back to "Follow".
    const cur = store.getQuery(api.follows.getMyFollowingIds, {}) ?? lastFollowingIds ?? []
    const next = cur.includes(userId) ? cur.filter((id) => id !== userId) : [...cur, userId]
    store.setQuery(api.follows.getMyFollowingIds, {}, next)
  })

  const known = ids !== undefined && ids !== null
  return {
    known: known || initial !== undefined,
    isFollowing: known ? ids.includes(userId) : !!initial,
    toggle: async () => {
      if (!handle) return
      try {
        await follow({ handle })
      } catch {}
    },
  }
}

export function FollowButton({ handle, userId }: { handle: string | null; userId: string }) {
  const { t } = useTranslation()
  const { known, isFollowing, toggle } = useFollowToggle(handle, userId)
  if (!handle) return null
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        void toggle()
      }}
      disabled={!known}
      className={cn(
        'shrink-0 cursor-pointer rounded-full px-3 py-1 text-xs font-bold transition-all disabled:opacity-50',
        isFollowing
          ? 'bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
          : 'bg-primary text-white dark:text-[#0a0a0a] hover:bg-primary/90',
      )}
    >
      {!known ? '…' : isFollowing ? t('leaderboard.following') : t('leaderboard.follow')}
    </button>
  )
}
